<template>
    <ListingWrapper
        :title="translations.title"
        :loading="isLoadingTable"
        :isTableLoading="isLoadingTable"
        :pagination="pagination"
        class="reports-listing"
        @update-pagination="updatePagination"
    >
        <template #listingTabs>
            <HeadingTabs
                type="filter"
                :tabs="listingTabs"
                :activeTab="activeTab"
                @tab-clicked="handleTabChange"
            />
        </template>
        <template #settings>
            <ReportsListingSettingsHeader
                :searchValue="searchValue"
                :filters="filters"
                :totalResults="totalResults"
                @search="searchAttributes"
                @updateActiveFilters="updateActiveFilters"
                @updateFilter="updateFilter"
            />
        </template>
        <template #table>
            <CompassTable size="large" :border="false" :isCheckable="false">
                <ReportsListingTableHead :columns="columns" @sort-table-head="handleSort" />
                <td
                    v-if="!reports.length"
                    colspan="100%"
                    class="reports-listing__empty-state__no-result"
                >
                    <TableEmptyStateNoResult
                        v-if="shouldShowNoResultState"
                        @reset-filters="resetReports"
                    />
                    <ReportsListingEmptyStateFavorite
                        v-else-if="shouldShowFavoriteEmptyState"
                        @redirectToAllReports="handleTabChange('all')"
                    />
                </td>
                <CompassTableBody v-else>
                    <ReportsListingTableRow
                        v-for="(report, index) in reports"
                        :key="index"
                        :report="report"
                        :cumulioAuth="cumulioAuth"
                        :columns="columns"
                        @update-favorite="updateFavorite"
                    />
                </CompassTableBody>
            </CompassTable>
        </template>
    </ListingWrapper>
</template>
<script>
import HeadingTabs from 'vcomponents/common/HeadingTabs/HeadingTabs.vue';
import ListingWrapper from 'vcomponents/common/listing/ListingWrapper.vue';
import ReportsListingEmptyStateFavorite from 'vcomponents/reports/listing/table/ReportsListingEmptyStateFavorite.vue';

import ReportsApi from 'api/reports/reports';
import TableEmptyStateNoResult from 'vcomponents/common/table/TableEmptyStateNoResult.vue';
import ReportsListingSettingsHeader from './table/ReportsListingSettingsHeader.vue';
import ReportsListingTableHead from './table/ReportsListingTableHead.vue';
import ReportsListingTableRow from './table/ReportsListingTableRow.vue';
import { REPORTS_CATEGORIES } from 'Dictionaries/reports/reportsCategories.js';

import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');
const SORT_ORDERS = {
    ASC: 'asc',
    DESC: 'desc',
    NEUTRAL: '',
};
const ORDER_TRANSITIONS = {
    [SORT_ORDERS.ASC]: SORT_ORDERS.DESC,
    [SORT_ORDERS.DESC]: SORT_ORDERS.NEUTRAL,
    [SORT_ORDERS.NEUTRAL]: SORT_ORDERS.ASC,
};
const DEFAULT_COLUMN = 'name';

export default {
    components: {
        ReportsListingSettingsHeader,
        TableEmptyStateNoResult,
        ReportsListingEmptyStateFavorite,
        ReportsListingTableHead,
        ReportsListingTableRow,
        HeadingTabs,
        ListingWrapper,
    },
    data() {
        return {
            translations: {
                title: this.$t.__('reports.ReportsListing.title'),
            },
            listingTabs: [
                { id: 'all', label: this.$t.__('reports.ReportsListing.tabs_all') },
                { id: 'favorites', label: this.$t.__('reports.ReportsListing.tabs_my_favorites') },
            ],
            activeTab: 'all',
            allReports: null,
            reports: [],
            cumulioAuth: {},
            columns: [
                {
                    order: 0,
                    label: '',
                    code: 'favorite',
                    fixed: 'left',
                    alignContent: 'center',
                    maxWidth: '64px',
                    minWidth: '64px',
                },
                {
                    order: 1,
                    label: this.$t.__('common.labels.name'),
                    code: 'name',
                    sortable: { direction: SORT_ORDERS.ASC, orderBy: 'name' },
                },
                {
                    order: 2,
                    label: this.$t.__('common.labels.category'),
                    code: 'category',
                    sortable: { direction: SORT_ORDERS.NEUTRAL, orderBy: 'categoryID' },
                },
            ],
            isLoadingTable: true,
            searchValue: '',
            sort: [],
            pagination: {
                limit: 50,
                offset: 0,
                total: 0,
                currentPage: 1,
            },
            filters: [],
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),
        filteredReports() {
            if (!this.allReports) {
                return [];
            }
            let filtered = [...this.allReports];
            // Filter by active tab (all or favorites)
            if (this.activeTab === 'favorites') {
                filtered = filtered.filter((report) => report.favorite);
            }
            // Filter by active filters
            for (const filter of this.filters) {
                if (filter.isActive && filter.value.length > 0) {
                    filtered = filtered.filter((report) =>
                        filter.value.includes(report[filter.code]),
                    );
                }
            }
            // Filter by search value (name only)
            if (this.searchValue) {
                const searchLower = this.searchValue.toLowerCase();
                filtered = filtered.filter((report) =>
                    report.name.toLowerCase().includes(searchLower),
                );
            }
            return filtered;
        },
        sortedReports() {
            const reports = [...this.filteredReports];
            reports.sort((a, b) => {
                for (const { column, order } of this.sort) {
                    const aValue = a[column];
                    const bValue = b[column];
                    if (aValue < bValue) {
                        return order === 'asc' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return order === 'asc' ? 1 : -1;
                    }
                }
                return 0;
            });
            return reports;
        },
        paginatedReports() {
            const start = this.pagination.offset;
            const end = start + this.pagination.limit;
            return this.sortedReports.slice(start, end);
        },
        totalResults() {
            return this.filteredReports.length;
        },
        columnsMap() {
            const map = new Map();
            for (const col of this.columns) {
                if (col.sortable) {
                    map.set(col.sortable.orderBy, col);
                    map.set(col.code, col);
                }
            }
            return map;
        },
        hasActiveFilters() {
            return (
                this.filters.some((filter) => filter.isActive && filter.value.length > 0) ||
                this.searchValue
            );
        },
        shouldShowNoResultState() {
            return (
                this.activeTab === 'all' ||
                !this.allReports ||
                this.allReports.length === 0 ||
                (this.hasActiveFilters && this.filteredReports.length === 0)
            );
        },
        shouldShowFavoriteEmptyState() {
            return (
                this.activeTab === 'favorites' &&
                this.allReports &&
                this.allReports.length > 0 &&
                !this.allReports.some((report) => report.favorite)
            );
        },
    },
    created() {
        this.initializeSort();
        this.getReports();
    },
    methods: {
        getReports() {
            this.isLoadingTable = true;
            ReportsApi.getReports()
                .then((response) => {
                    const data = response.data.data;
                    const aggregations = response.data.aggregations;
                    this.allReports = data.map((report) => ({
                        id: report.dashboard_id ?? null,
                        name: this.getReportName(report.name),
                        path: report.path,
                        description: report.subtitle ?? null,
                        type: report.type, // cumulio or legacy
                        category: report.tags[0],
                        categoryID: REPORTS_CATEGORIES[report.tags[0]]?.id,
                        new: report.is_new ?? false,
                        favorite: report.is_favorite ?? false,
                    }));
                    if (aggregations.cumulio && aggregations.cumulio.key) {
                        this.cumulioAuth = aggregations.cumulio;
                    }
                    this.initializeFilters();
                    this.updateReports();
                })
                .catch((error) => {
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('common.labels.toast.error'),
                    });
                })
                .finally(() => {
                    this.isLoadingTable = false;
                });
        },
        getReportName(name) {
            // No longer needed once the first deployment is done
            const parts = name.split('>');
            if (parts.length >= 3) {
                return parts[2].trim();
            }
            return name.trim();
        },
        searchAttributes(value) {
            this.searchValue = value;
            this.updateReports();
        },
        handleTabChange(tabId) {
            this.activeTab = tabId;
            this.updateReports();
        },
        updateReports() {
            this.pagination.total = this.totalResults;
            this.pagination.offset =
                this.pagination.currentPage === 1
                    ? 0
                    : (this.pagination.currentPage - 1) * this.pagination.limit;
            this.reports = this.paginatedReports;
        },
        updatePagination(newPagination) {
            this.pagination = {
                ...newPagination,
                offset: (newPagination.currentPage - 1) * newPagination.limit,
            };
            this.updateReports();
        },
        initializeSort() {
            for (const col of this.columns) {
                if (col.sortable) {
                    col.sortable.direction = SORT_ORDERS.NEUTRAL;
                }
            }
            this.sort = [{ column: DEFAULT_COLUMN, order: SORT_ORDERS.ASC }];
            const nameColumn = this.columnsMap.get(DEFAULT_COLUMN);
            if (nameColumn && nameColumn.sortable) {
                nameColumn.sortable.direction = SORT_ORDERS.ASC;
            }
        },
        handleSort(sortParams) {
            for (const col of this.columns) {
                if (col.sortable) {
                    col.sortable.direction = SORT_ORDERS.NEUTRAL;
                }
            }
            const existingSort = this.sort.find((s) => s.column === sortParams.orderBy);
            if (existingSort) {
                const newOrder = ORDER_TRANSITIONS[existingSort.order];
                if (newOrder) {
                    this.sort = [{ column: sortParams.orderBy, order: newOrder }];
                    const column = this.columnsMap.get(sortParams.orderBy);
                    if (column) {
                        column.sortable.direction = newOrder;
                    }
                } else {
                    this.sort = [{ column: DEFAULT_COLUMN, order: SORT_ORDERS.ASC }];
                    const nameColumn = this.columnsMap.get(DEFAULT_COLUMN);
                    if (nameColumn && nameColumn.sortable) {
                        nameColumn.sortable.direction = SORT_ORDERS.ASC;
                    }
                }
            } else {
                this.sort = [{ column: sortParams.orderBy, order: SORT_ORDERS.ASC }];
                const column = this.columnsMap.get(sortParams.orderBy);
                if (column) {
                    column.sortable.direction = SORT_ORDERS.ASC;
                }
            }
            this.updateReports();
        },
        initializeFilters() {
            this.filters = [
                {
                    activeLabel: this.$t.__('common.labels.category'),
                    code: 'category',
                    components: 'ListingFiltersSelect',
                    displayDropdown: false,
                    isActive: false,
                    label: this.$t.__('common.labels.category'),
                    options: Object.values(REPORTS_CATEGORIES).map((category) => ({
                        id: category.id,
                        label: this.$t.__(category.label),
                        color: category.color,
                    })),
                    optionsSelected: [],
                    value: [],
                },
            ];
        },
        updateActiveFilters(filterToUpdate, updateListing = false) {
            for (const filter of this.filters) {
                filter.displayDropdown = false;
                if (filter.code === filterToUpdate.code) {
                    filter.isActive = filterToUpdate.isActive;
                    filter.value = filterToUpdate.value;
                    filter.optionsSelected = filterToUpdate.optionsSelected;
                    filter.displayDropdown = filterToUpdate.displayDropdown;
                }
            }
            if (updateListing) {
                this.updateReports();
            }
        },
        updateFilter({ filterCode, filterValue, filterOptionsSelected }) {
            for (const filter of this.filters) {
                if (filter.code === filterCode) {
                    filter.value = filterValue;
                    filter.optionsSelected = filterOptionsSelected;
                }
            }
            this.updateReports();
        },
        updateFavorite({ reportId, isFavorite }) {
            const report = this.allReports.find((report) => report.id === reportId);
            if (report) {
                report.favorite = isFavorite;
            }
            this.updateReports();
        },
        resetFilters() {
            for (const filter of this.filters) {
                filter.isActive = false;
                filter.value = [];
                filter.optionsSelected = [];
                filter.displayDropdown = false;
            }
        },
        resetReports() {
            this.searchValue = '';
            this.activeTab = 'all';
            this.initializeSort();
            this.resetFilters();
            this.updateReports();
        },
    },
};
</script>

<style lang="scss" scoped>
.reports-listing {
    &__empty-state {
        height: 560px;
        display: flex;
        justify-content: center;
        background-color: var(--colors-common-background-default);
        border: 1px solid var(--colors-common-border-default);
        border-radius: var(--spacing-0);

        &__no-result {
            padding: 112px;
        }
    }
}
</style>
